import {Dropdown, Tooltip} from 'platform/components';
import {Icon, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState, type ReactNode} from 'react';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {Preset} from '../../../../types/Api';
import {PRESET_TAB_STYLES} from '../../constants/PRESET_TAB_STYLES';
import {PresetApiStateEnum} from '../../constants/PresetApiStateEnum';
import {getPresetColorClasses} from '../../utils/getPresetColorClasses';
import {PresetActionsButton} from '../PresetActionsButton/PresetActionsButton';
import {PresetSelectionIndicator} from '../PresetSelectionIndicator/PresetSelectionIndicator';
import {PresetTabLabel} from '../PresetTabLabel/PresetTabLabel';

export type PresetButtonProps = TestIdProps & {
  preset: Preset;
  active: boolean;
  unsaved: boolean;
  state: PresetApiStateEnum;
  copyIsLoading?: boolean;
  rowCount: number | undefined;
  actions?: ReactNode | ((closeMenu: () => void) => ReactNode);
  isDisabled?: boolean;
  /** Optional preset color — used as the tab background. */
  color?: string;
  onClick: () => void;
};

export function PresetButton(props: PresetButtonProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Loading adds no status icon: the grid already signals
  // loading via the header progress bar and the cell skeletons, and a spinner
  // inserted before the label changed the pill width — making the whole preset
  // row jump (T20-134467 OG feedback).
  const stateIcon = match(props.state)
    .with(PresetApiStateEnum.success, () => <Icon value="action/check_circle" />)
    .with(PresetApiStateEnum.error, () => <Icon value="alert/error" />)
    .otherwise(() => null);

  const colorClasses = getPresetColorClasses(props.color, props.active);

  const buttonDisabledClass = props.isDisabled ? 'opacity-60 pointer-events-none' : '';

  return (
    <div
      role="group"
      className={`${PRESET_TAB_STYLES.root} ${colorClasses} ${buttonDisabledClass}`}
      data-testid={props['data-testid']}
    >
      <Show when={stateIcon}>
        <span
          className="flex items-center flex-shrink-0 text-inherit"
          data-testid={suffixTestId('preset-status-icon', props)}
        >
          {stateIcon}
        </span>
      </Show>

      <Tooltip
        childrenWrapperDisplay="flex"
        placement="top"
        label={
          props.rowCount !== undefined
            ? `${props.preset.title} — ${props.rowCount}`
            : props.preset.title
        }
        description={props.preset.description}
      >
        <button
          type="button"
          onClick={props.onClick}
          aria-pressed={props.active}
          disabled={props.isDisabled}
          className={`${PRESET_TAB_STYLES.button} ${buttonDisabledClass}`}
        >
          <PresetSelectionIndicator
            active={props.active}
            data-testid={suffixTestId('active-indicator', props)}
          />
          <PresetTabLabel
            label={props.unsaved ? `* ${props.preset.title}` : props.preset.title}
            active={props.active}
          />
          <Show when={props.rowCount !== undefined || Boolean(props.actions)}>
            <span className={`${PRESET_TAB_STYLES.count} ${PRESET_TAB_STYLES.countColor}`}>
              {props.rowCount}
            </span>
          </Show>
        </button>
      </Tooltip>

      <Show when={props.actions}>
        <div
          className={`${PRESET_TAB_STYLES.actions} ${isMenuOpen ? '' : PRESET_TAB_STYLES.hiddenActions}`}
        >
          <Dropdown
            isLazy
            strategy="fixed"
            isOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => {
              if (!props.copyIsLoading) {
                setIsMenuOpen(false);
              }
            }}
            data-testid={suffixTestId('preset-actions-dropdown', props)}
            dropdownControl={
              <PresetActionsButton
                variant="tab"
                isDisabled={props.isDisabled}
                isExpanded={isMenuOpen}
                data-testid={suffixTestId('actions-iconButton', props)}
              />
            }
          >
            <Show when={isMenuOpen}>
              {typeof props.actions === 'function'
                ? props.actions(() => setIsMenuOpen(false))
                : props.actions}
            </Show>
          </Dropdown>
        </div>
      </Show>
    </div>
  );
}
